/**
 * Welke collecties de prullenbak afloopt, hoe ze in het Nederlands heten en in
 * welk veld hun titel staat.
 *
 * De prullenbak leest de collecties rechtstreeks in plaats van een eigen
 * registratie bij te houden. Dat is bewust: verwijderen gebeurt op ruim dertig
 * plekken in de code, en lang niet allemaal via `softDeleteDoc`. Een registratie
 * zou dus alleen tonen wat toevallig langs die ene helper kwam — en niets van de
 * honderden dingen die vóór de prullenbak al verwijderd waren.
 *
 * De lijst staat grofweg alfabetisch, maar met de ouder vóór zijn kinderen
 * (`Adresses` vóór `AdressMaterials`, `Tasks` vóór `TaskNotes`, `Personas` vóór
 * `PersonaReports`). Zet een nieuwe collectie bij zijn familie, niet onderaan.
 *
 * `titleFields` staat op volgorde: het eerste veld met tekst wint. Collecties
 * gebruiken hier verschillende namen voor (`Effect`, `Activity`, `Organisation`),
 * en sommige documenten hebben helemaal geen titel — dan valt de lijst terug op
 * de Nederlandse naam van de collectie.
 *
 * AI-CONTEXT: See docs/plans/prullenbak.md
 * AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in —
 * een collectie die hier ontbreekt is na een zachte delete onvindbaar, ook al
 * belooft de DeleteModal (`softDelete`) dat hij terug te zetten is.
 *
 * @typedef {object} TrashCollection
 * @property {string} collection - Naam van de Firestore-collectie
 * @property {string} label - Nederlandse naam, enkelvoud
 * @property {Array<string>} titleFields - Velden waarin de titel kan staan
 */

/** @type {Array<TrashCollection>} */
const TRASH_COLLECTIONS = [
    { collection: 'Activities', label: 'Activiteit', titleFields: ['Activity', 'Title', 'Name'] },
    { collection: 'Adresses', label: 'Huishouden', titleFields: ['Title', 'Adress', 'Street'] },
    { collection: 'AdressMaterials', label: 'Materiaal bij huishouden', titleFields: ['Title', 'Name'] },
    { collection: 'AdressNotes', label: 'Notitie bij huishouden', titleFields: ['Title', 'Note'] },
    { collection: 'Anecdotes', label: 'Anekdote', titleFields: ['Title', 'Anecdote'] },
    { collection: 'CompagnyCosts', label: 'Kosten', titleFields: ['Title', 'Name'] },
    { collection: 'ContactMoments', label: 'Contactmoment', titleFields: ['Title', 'Type'] },
    { collection: 'DashboardItems', label: 'Dashboardkaart', titleFields: ['Title', 'Name'] },
    { collection: 'Events', label: 'Gebeurtenis', titleFields: ['Title', 'Description'] },
    // Een registratie van een activiteit: één document per veld of output, met
    // dezelfde SessionID. Geen titelveld, dus de lijst toont 'Registratie';
    // het eerste document is de ouder en zet de hele sessie terug.
    { collection: 'EventResults', label: 'Registratie', titleFields: ['Title'] },
    { collection: 'IntakeForms', label: 'Intakeformulier', titleFields: ['Title', 'Name'] },
    { collection: 'InterviewEvidence', label: 'Interviewfragment', titleFields: ['Quote', 'Title'] },
    { collection: 'Kpis', label: 'Doelstelling', titleFields: ['Title', 'Kpi', 'Name'] },
    { collection: 'LiveReports2', label: 'Rapportage', titleFields: ['Name', 'Title'] },
    // Alleen een opslag-URL en een type; geen titel, dus de lijst toont 'Media'.
    { collection: 'LiveReportMedia', label: 'Media', titleFields: ['Title'] },
    // Zacht verwijderd sinds inbox #284; de eigen naam wint, zoals in materialDisplayName.
    { collection: 'Materials', label: 'Materiaal', titleFields: ['DisplayTitle', 'Title'] },
    { collection: 'MeasureMoments', label: 'Meetmoment', titleFields: ['Title', 'ResearchTitle'] },
    { collection: 'MultipleQuestionOptions', label: 'Antwoordoptie', titleFields: ['Option', 'Title'] },
    { collection: 'OutputEffects', label: 'Effect', titleFields: ['Effect', 'Title', 'Name'] },
    { collection: 'Outputs', label: 'Output', titleFields: ['Output', 'Title', 'Name'] },
    { collection: 'Personas', label: 'Deelnemer', titleFields: ['Name'] },
    // Een afwezigheid heeft geen titelveld: de notitie is optioneel, en anders
    // is de reden ('vakantie', 'ziek', 'verlof', 'overig') het enige houvast.
    { collection: 'PersonaAbsences', label: 'Geplande afwezigheid', titleFields: ['Note', 'Reason'] },
    // Een persoonlijk doel van een deelnemer (tabblad Doelen).
    { collection: 'PersonaGoals', label: 'Doel van deelnemer', titleFields: ['Title', 'Goal', 'Description'] },
    { collection: 'PersonaReports', label: 'Notitie bij deelnemer', titleFields: ['Title', 'Report'] },
    // Een aanwezigheidsrapport heeft geen titel; de datum ('maandag 13 oktober
    // 2026') is wat de gebruiker ervan kent.
    { collection: 'PersonaPresenceReports', label: 'Aanwezigheidsrapport', titleFields: ['Date'] },
    { collection: 'PersonaRoutes', label: 'Traject', titleFields: ['Title'] },
    // Een tarief van een deelnemer (tabblad Gegevens); Title is 'bedrag vanaf datum'.
    { collection: 'PersonaTariffs', label: 'Tarief van deelnemer', titleFields: ['Title'] },
    // Een extra regel op de facturatie van een maand (pagina Facturatie).
    { collection: 'PersonaBillingAdjustments', label: 'Extra regel facturatie', titleFields: ['Title', 'Reason'] },
    // Een gegeven (titel + waarde) bij een huishouden of deelnemer; PersonaID wijst naar beide.
    { collection: 'PersonaData', label: 'Gegeven', titleFields: ['Title', 'Value'] },
    { collection: 'ProjectNotes', label: 'Notitie bij project', titleFields: ['Title', 'Body'] },
    { collection: 'Products', label: 'Product', titleFields: ['Title', 'Name', 'Product'] },
    { collection: 'Questionnaires', label: 'Vragenlijst', titleFields: ['Title'] },
    { collection: 'QuestionnaireFields', label: 'Vraag', titleFields: ['Question', 'Title', 'Type'] },
    // Kinderen van een matrixvraag. Een rij heeft bij een tweezijdige stelling
    // ook een rechtertekst; de linker is de stelling zelf.
    { collection: 'MatrixQuestionRows', label: 'Stelling', titleFields: ['Title', 'TitleRight', 'Question'] },
    { collection: 'MatrixQuestionColumns', label: 'Antwoordkolom', titleFields: ['Title', 'Question'] },
    // De stelling vóór de vraag: bij een matrixvraag dragen alle antwoorden
    // dezelfde `FieldTitle`, en `Input` is daar alleen een cijfer.
    { collection: 'QuestionnairesResponses', label: 'Antwoord', titleFields: ['RowTitle', 'FieldTitle', 'Input'] },
    { collection: 'Quotes', label: 'Quote', titleFields: ['Quote', 'Title'] },
    { collection: 'Research', label: 'Onderzoek', titleFields: ['Title', 'Name'] },
    // Een sessie heeft geen titel; de lijst toont dan 'Sessie'.
    { collection: 'Sessions', label: 'Sessie', titleFields: ['Title'] },
    { collection: 'Stakeholders', label: 'Doelgroep', titleFields: ['Organisation', 'Title', 'Name'] },
    { collection: 'StorytellingInterviews', label: 'Interview', titleFields: ['Title', 'Name', 'Type'] },
    { collection: 'Tasks', label: 'Afspraak', titleFields: ['Title', 'Name'] },
    { collection: 'TaskNotes', label: 'Notitie bij afspraak', titleFields: ['Title', 'Note'] },
    { collection: 'TocMechanisms', label: 'Werkzaam mechanisme', titleFields: ['Title', 'Mechanism', 'Category'] },
    { collection: 'Wervingsacties', label: 'Wervingsactie', titleFields: ['Title', 'Name'] },
    { collection: 'Wervingsregistraties', label: 'Wervingsregistratie', titleFields: ['Title', 'Name'] },
]

/** @type {Record<string, TrashCollection>} */
const BY_NAME = TRASH_COLLECTIONS.reduce((acc, c) => ({ ...acc, [c.collection]: c }), {})

/**
 * Geeft de Nederlandse naam van een collectie.
 *
 * Een onbekende collectie levert de technische naam terug: liever iets
 * herkenbaars voor wie de database kent dan een leeg vakje.
 *
 * @param {string} collection - Naam van de Firestore-collectie
 * @returns {string}
 */
const collectionLabel = (collection) => BY_NAME[collection]?.label || collection || ''

/**
 * Zoekt de titel van een document op, zoals de gebruiker die kent.
 *
 * Zonder titel is de lijst een rij document-ids; de Nederlandse naam van de
 * collectie is dan het enige houvast dat de gebruiker heeft.
 *
 * @param {string} collection - Naam van de Firestore-collectie
 * @param {object} data - Het document
 * @returns {string}
 */
const trashItemTitle = (collection, data) => {
    const velden = BY_NAME[collection]?.titleFields || ['Title', 'Name']
    const gevonden = velden
        .map((veld) => data?.[veld])
        .find((waarde) => typeof waarde === 'string' && waarde.trim())

    if (!gevonden) return collectionLabel(collection)
    return gevonden.trim()
}

export { TRASH_COLLECTIONS, collectionLabel, trashItemTitle }
export default TRASH_COLLECTIONS
